<template>
    <v-card class="selecteur-dents">
        <v-card-title class="d-flex align-center">
            <span class="font-weight-bold">Selection des dents</span>
            <v-spacer></v-spacer>
            <v-btn size="small" variant="text" @click="toutEffacer">Tout effacer</v-btn>
        </v-card-title>

        <v-card-text>
            <!-- Schema dentaire : dents permanentes (2 arcades) -->
            <svg class="schema-dentaire" :viewBox="viewBoxPermanentes" xmlns="http://www.w3.org/2000/svg">
                <!-- Arcade superieure -->
                <g v-for="(num, i) in rangeeHaut" :key="'h' + num">
                    <rect class="dent"
                          :class="{ 'dent-active': estSelectionnee(num) }"
                          :x="xDent(i)" :y="yHaut" :width="largeurDent" :height="hauteurDent"
                          rx="6" ry="6"
                          @click="toggle(num)"></rect>
                    <text class="dent-label"
                          :class="{ 'dent-label-active': estSelectionnee(num) }"
                          :x="xDent(i) + largeurDent / 2" :y="yHaut + hauteurDent / 2"
                          text-anchor="middle" dominant-baseline="central"
                          @click="toggle(num)">{{ num }}</text>
                </g>

                <!-- Arcade inferieure -->
                <g v-for="(num, i) in rangeeBas" :key="'b' + num">
                    <rect class="dent"
                          :class="{ 'dent-active': estSelectionnee(num) }"
                          :x="xDent(i)" :y="yBas" :width="largeurDent" :height="hauteurDent"
                          rx="6" ry="6"
                          @click="toggle(num)"></rect>
                    <text class="dent-label"
                          :class="{ 'dent-label-active': estSelectionnee(num) }"
                          :x="xDent(i) + largeurDent / 2" :y="yBas + hauteurDent / 2"
                          text-anchor="middle" dominant-baseline="central"
                          @click="toggle(num)">{{ num }}</text>
                </g>
            </svg>

            <!-- Dents temporaires -->
            <p class="text-body-2 font-weight-bold mt-4 mb-1">Dents temporaires</p>
            <svg class="schema-dentaire" :viewBox="viewBoxTemporaires" xmlns="http://www.w3.org/2000/svg">
                <!-- Arcade superieure temporaire -->
                <g v-for="(num, i) in rangeeHautTemp" :key="'th' + num">
                    <rect class="dent"
                          :class="{ 'dent-active': estSelectionnee(num) }"
                          :x="xDentTemp(i)" :y="yHaut" :width="largeurDent" :height="hauteurDent"
                          rx="6" ry="6"
                          @click="toggle(num)"></rect>
                    <text class="dent-label"
                          :class="{ 'dent-label-active': estSelectionnee(num) }"
                          :x="xDentTemp(i) + largeurDent / 2" :y="yHaut + hauteurDent / 2"
                          text-anchor="middle" dominant-baseline="central"
                          @click="toggle(num)">{{ num }}</text>
                </g>

                <!-- Arcade inferieure temporaire -->
                <g v-for="(num, i) in rangeeBasTemp" :key="'tb' + num">
                    <rect class="dent"
                          :class="{ 'dent-active': estSelectionnee(num) }"
                          :x="xDentTemp(i)" :y="yBas" :width="largeurDent" :height="hauteurDent"
                          rx="6" ry="6"
                          @click="toggle(num)"></rect>
                    <text class="dent-label"
                          :class="{ 'dent-label-active': estSelectionnee(num) }"
                          :x="xDentTemp(i) + largeurDent / 2" :y="yBas + hauteurDent / 2"
                          text-anchor="middle" dominant-baseline="central"
                          @click="toggle(num)">{{ num }}</text>
                </g>
            </svg>

            <!-- Recapitulatif de la selection -->
            <p class="text-body-2 mt-3">
                Selection : <span class="font-weight-bold">{{ selectionTriee.join(', ') || 'aucune' }}</span>
            </p>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="annuler">Annuler</v-btn>
            <v-btn color="#119393" @click="valider">Valider</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
    import { defineComponent } from 'vue';

    export default defineComponent({
        name: 'SelecteurDents',

        props: {
            modelValue: {
                type: String,
                default: '',
            },
        },

        emits: ['update:modelValue', 'close'],

        data() {
            return {
                // Selection locale : { [numero]: true } (Vue 3 reagit mieux sur un objet simple).
                selection: {},

                // Geometrie du schema (unites SVG ; le SVG scale en largeur 100%).
                largeurDent: 34,
                hauteurDent: 40,
                ecartDent: 4,
                yHaut: 4,
                yBas: 52,

                // Numerotation FDI.
                // Rangee du haut : haut-droit 18..11 puis haut-gauche 21..28 (gauche -> droite).
                rangeeHaut: [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
                // Rangee du bas : bas-droit 48..41 puis bas-gauche 31..38 (gauche -> droite).
                rangeeBas: [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38],

                // Dents temporaires (enfant).
                // Haut : haut-droit 55..51 puis haut-gauche 61..65.
                rangeeHautTemp: [55, 54, 53, 52, 51, 61, 62, 63, 64, 65],
                // Bas : bas-droit 85..81 puis bas-gauche 71..75.
                rangeeBasTemp: [85, 84, 83, 82, 81, 71, 72, 73, 74, 75],
            };
        },

        computed: {
            pasDent() {
                return this.largeurDent + this.ecartDent;
            },
            viewBoxPermanentes() {
                const largeur = this.rangeeHaut.length * this.pasDent;
                const hauteur = this.yBas + this.hauteurDent + 4;
                return `0 0 ${largeur} ${hauteur}`;
            },
            viewBoxTemporaires() {
                const largeur = this.rangeeHautTemp.length * this.pasDent;
                const hauteur = this.yBas + this.hauteurDent + 4;
                return `0 0 ${largeur} ${hauteur}`;
            },
            selectionTriee() {
                return Object.keys(this.selection)
                    .filter((k) => this.selection[k])
                    .map((k) => parseInt(k, 10))
                    .sort((a, b) => a - b);
            },
        },

        watch: {
            modelValue() {
                this.parserSelection();
            },
        },

        mounted() {
            this.parserSelection();
        },

        methods: {
            // Parse modelValue ("11,12,24") en objet de selection.
            parserSelection() {
                const sel = {};
                (this.modelValue || '')
                    .split(',')
                    .map((p) => p.trim())
                    .filter((p) => p !== '')
                    .forEach((p) => { sel[p] = true; });
                this.selection = sel;
            },

            estSelectionnee(num) {
                return !!this.selection[num];
            },

            toggle(num) {
                const cle = String(num);
                if (this.selection[cle]) {
                    delete this.selection[cle];
                } else {
                    this.selection[cle] = true;
                }
            },

            xDent(index) {
                return index * this.pasDent + this.ecartDent / 2;
            },

            xDentTemp(index) {
                return index * this.pasDent + this.ecartDent / 2;
            },

            toutEffacer() {
                this.selection = {};
            },

            valider() {
                this.$emit('update:modelValue', this.selectionTriee.join(','));
                this.$emit('close');
            },

            annuler() {
                this.$emit('close');
            },
        },
    });
</script>

<style scoped>
    .selecteur-dents {
        max-width: 100%;
    }

    .schema-dentaire {
        width: 100%;
        height: auto;
        display: block;
    }

    .dent {
        fill: #ffffff;
        stroke: #119393;
        stroke-width: 1.5;
        cursor: pointer;
    }

    .dent:hover {
        fill: #e0f2f2;
    }

    .dent-active {
        fill: #119393;
        stroke: #0d6e6e;
    }

    .dent-label {
        font-size: 13px;
        fill: #434343;
        cursor: pointer;
        user-select: none;
        pointer-events: none;
    }

    .dent-label-active {
        fill: #ffffff;
    }
</style>
